/*
 * Registers the service worker (src/service-worker.ts) in production builds
 * and tells the page when a newer build is installed and waiting.
 *
 * Development never registers one: a worker serving cached bundles under the
 * dev server is how stale-code bugs that "only happen on my machine" start,
 * and Playwright's route mocks cannot see requests a worker answers.
 */

type WaitingListener = (registration: ServiceWorkerRegistration) => void;

// Backoffice tabs stay open all day; without a nudge the browser only looks
// for a new worker on navigation.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

const listeners = new Set<WaitingListener>();
let waitingRegistration: ServiceWorkerRegistration | null = null;

function announceWaiting(registration: ServiceWorkerRegistration) {
    waitingRegistration = registration;
    listeners.forEach((listener) => listener(registration));
}

/**
 * Calls `listener` whenever a new build is installed and waiting to take
 * over — straight away if one already is. Returns an unsubscribe function.
 */
export function onUpdateWaiting(listener: WaitingListener): () => void {
    listeners.add(listener);
    if (waitingRegistration?.waiting) listener(waitingRegistration);
    return () => {
        listeners.delete(listener);
    };
}

/** Hands control to the waiting build and reloads this tab onto it. */
export function applyUpdate(registration: ServiceWorkerRegistration) {
    const waiting = registration.waiting;
    if (!waiting) {
        window.location.reload();
        return;
    }
    navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => window.location.reload(),
        { once: true },
    );
    waiting.postMessage({ type: 'SKIP_WAITING' });
}

export function register() {
    if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) return;

    // A worker cannot be served from a different origin than the page.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) return;

    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register(`${process.env.PUBLIC_URL}/service-worker.js`)
            .then((registration) => {
                // A build that finished installing while no tab was open.
                if (registration.waiting && navigator.serviceWorker.controller) {
                    announceWaiting(registration);
                }

                registration.addEventListener('updatefound', () => {
                    const installing = registration.installing;
                    if (!installing) return;
                    installing.addEventListener('statechange', () => {
                        // With no controller this is the first install, not
                        // an update — nothing to offer.
                        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                            announceWaiting(registration);
                        }
                    });
                });

                window.setInterval(() => {
                    registration.update().catch(() => undefined);
                }, UPDATE_CHECK_INTERVAL_MS);
            })
            .catch((error) => {
                console.error('Service worker registration failed:', error);
            });
    });
}

export function unregister() {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready
        .then((registration) => registration.unregister())
        .catch(() => undefined);
}
